import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import EventSourceChip from "@/components/events/EventSourceChip";
import PrizeDrawCard from "@/components/events/PrizeDrawCard";
import { Loader2, MapPin, Calendar, Ticket, Phone, Mail, Bell } from "lucide-react";
import { toast } from "sonner";

export default function EventDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [registered, setRegistered] = useState(false);
  const q = useQuery({
    queryKey: ["event", id],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/events/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const riMut = useMutation({
    mutationFn: async () => {
      // Backend requires a real payload — send empty {} for authenticated
      // users (contact copied from JWT). Anonymous users hit the info
      // toast below before ever reaching the mutation.
      const { data } = await sharedApi.post(
        `/classifieds/events/${id}/register-interest`,
        {}
      );
      return data;
    },
    onSuccess: () => {
      setRegistered(true);
      toast.success("Nice — the organiser will keep you posted.");
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 401) {
        toast.info("Sign in first so the organiser can reach you.");
      } else if (status === 409) {
        setRegistered(true);
        toast.info("You've already registered interest for this event.");
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't register your interest");
      }
    },
  });

  const handleRegisterInterest = () => {
    if (!user) {
      toast.info("Sign in first so the organiser can reach you.");
      return;
    }
    riMut.mutate();
  };

  if (q.isLoading) return <div className="max-w-3xl mx-auto py-20 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>;
  if (q.isError || !q.data) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold text-slate-900">Event not found</h1>
      <Link to="/events" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">Back to events</Link>
    </div>
  );
  const e = q.data;
  const starts = e.starts_at ? new Date(e.starts_at) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title={e.title} description={(e.description || e.venue || "").slice(0, 160)} image={e.image_url} type="article" />
      <Breadcrumbs items={[{ label: "Events", to: "/events" }, { label: e.title }]} />

      <div className="mt-6 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {e.image_url && <img src={e.image_url} alt={e.title} className="w-full rounded-2xl shadow-card object-cover aspect-[16/10]" />}
          <h1 className="mt-6 font-display text-3xl md:text-4xl font-black text-slate-900">{e.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            {starts && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{starts.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}</span>}
            {(e.venue || e.address) && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{[e.venue, e.address].filter(Boolean).join(" · ")}</span>}
          </div>
          {e.description_html ? (
            <div
              data-testid="event-description-html"
              className="mt-4 text-slate-600 leading-relaxed rich-html"
              // Server-sanitized by the shared backend (whitelist: b,i,u,s,p,br,ul,ol,li,span
              // with inline color / background-color / font-size only). Safe to inject.
              dangerouslySetInnerHTML={{ __html: e.description_html }}
            />
          ) : (
            e.description && (
              <p data-testid="event-description-plain" className="mt-4 text-slate-600 whitespace-pre-wrap leading-relaxed">
                {e.description}
              </p>
            )
          )}
          {Array.isArray(e.locations) && e.locations.length > 0 && (
            <EventSessions locations={e.locations} />
          )}
          {e.source === "allsale.events" && (
            <div className="mt-4">
              <EventSourceChip source={e.source} priceLabel={null} variant="banner" />
            </div>
          )}
        </div>
        <div className="lg:col-span-4 space-y-4">
          {/* Iter 655 — free prize draw card (sponsored events only). */}
          <PrizeDrawCard eventId={e.id} />
          <div className="rounded-3xl bg-white shadow-card p-6">
            {e.price_label && (
              <>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Ticket</div>
                <div className="mt-1 font-display text-2xl font-black text-slate-900">{e.price_label}</div>
              </>
            )}
            <div className="mt-4 space-y-3">
              {e.ticket_url && (
                <a
                  href={e.ticket_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="event-get-tickets-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] transition-colors"
                >
                  <Ticket className="w-4 h-4" /> Get tickets
                </a>
              )}
              <button
                type="button"
                onClick={handleRegisterInterest}
                disabled={riMut.isPending || registered}
                data-testid="event-register-interest-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-70 transition-colors"
              >
                {riMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}{" "}
                {registered ? "You're registered" : "Register interest"}
              </button>
              {e.contact_phone && (
                <a href={`tel:${e.contact_phone}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] transition-colors">
                  <Phone className="w-4 h-4" /> {e.contact_phone}
                </a>
              )}
              {e.contact_email && (
                <a href={`mailto:${e.contact_email}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-slate-700 border border-soft hover:border-[color:var(--brand-blue)] transition-colors">
                  <Mail className="w-4 h-4" /> {e.contact_email}
                </a>
              )}
            </div>
            {e.organizer_name && <div className="mt-4 pt-4 border-t border-subtle text-xs text-slate-500">Organized by <span className="font-semibold text-slate-700">{e.organizer_name}</span></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventSessions({ locations }) {
  return (
    <section className="mt-8" data-testid="event-sessions">
      <h2 className="font-display text-xl font-bold text-slate-900">Sessions</h2>
      <div className="mt-3 space-y-3">
        {locations.map((loc, i) => (
          <div
            key={i}
            data-testid={`event-session-${i}`}
            className="rounded-2xl bg-white shadow-card p-4 md:p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
          >
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-slate-900 truncate">{loc.title}</div>
              {(loc.date || loc.time) && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  {formatSessionDate(loc.date)}
                  {loc.time && <span className="text-slate-400">·</span>}
                  {loc.time && <span>{loc.time}</span>}
                </div>
              )}
              {loc.address && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500">
                  <MapPin className="w-4 h-4" /> {loc.address}
                </div>
              )}
              {loc.content && (
                <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{loc.content}</p>
              )}
            </div>
            {loc.booking_url && (
              <a
                href={loc.booking_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`event-session-book-${i}`}
                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-4 py-2 transition-colors"
              >
                <Ticket className="w-4 h-4" /> Book
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function formatSessionDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}
